import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filterActions } from "@/entities/filter";
import { getLoadingStatesState } from "@/entities/loadingStates";
import styles from "./MagicModeButton.module.scss";

import { useAppCount } from "@/Hooks";
import Loader from "@/shared/ui/Loader/Loader";
import { ScanSearch } from "lucide-react";
import { styled, Tooltip } from "@mui/material";
import ImageSearchModal from "@/Views/ImageSearch/ImageSearchModal/ImageSearchModal";

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    background: "var(--white-color)",
    color: "var(--dark-title-color)",
    boxShadow: "0 16px 48px 0 rgba(0, 0, 0, 0.22)",
    fontSize: 11,
  },
}));

interface MagicModeButtonProps {
  showLottie: boolean;
  onButtonClick?: () => void;
}

const MagicModeButton: React.FC<MagicModeButtonProps> = ({
  showLottie,
  onButtonClick,
}) => {
  const dispatch = useDispatch();
  const { appCount } = useAppCount();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const searchButtonRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Get the loading state from Redux
  const {
    appReplayLoading,
    storeScreenLoading,
    appVideoAnimationLoading,
    webOnboardingLoading,
    similarScreenLoading,
    imageSearchLoading,
  } = useSelector(getLoadingStatesState);

  const handleClick = () => {
    dispatch(filterActions.activateFilterMagic());
    onButtonClick?.();
  };

  const isLoading =
    appReplayLoading ||
    storeScreenLoading ||
    appVideoAnimationLoading ||
    webOnboardingLoading ||
    similarScreenLoading ||
    imageSearchLoading;

  const handleSearchButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Get the position of the search button for modal positioning
    if (searchButtonRef.current) {
      const rect = searchButtonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + 5,
        left: rect.left,
        width: rect.width,
      });
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={styles.root}>
      <button
        style={{ position: "relative" }}
        className={styles.ocrModeContainer}
        onClick={handleClick}
        disabled={showLottie}
      >
        <div className={styles.ocrMode}>
          <div className={styles.ocrModeText}>
            <div className={styles.ocrModeTextIcon}>
              {isLoading ? (
                <Loader size={24} color="rgba(140, 140, 140, 0.5215686275)" />
              ) : (
                <img
                  src="/img/SmartSearchGrey.svg"
                  alt="searchImg"
                  loading="eager"
                  draggable={false}
                />
              )}
            </div>
            <h1 className={styles.magicTextDefault}>
              Search among {appCount} apps, UI elements and screen types
            </h1>
            <h3 className={styles.magicTextMobile}>it's magic</h3>
            <StyledTooltip title="Image search" placement="bottom">
              <div
                ref={searchButtonRef}
                className={styles.searchImageButton}
                onClick={handleSearchButtonClick}
              >
                <ScanSearch
                  size={24}
                  color="rgba(140, 140, 140, 0.5215686275)"
                />
              </div>
            </StyledTooltip>
          </div>
        </div>
      </button>

      {isModalOpen && (
        <ImageSearchModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          position={buttonPosition}
        />
      )}
    </div>
  );
};

export default MagicModeButton;
